import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Input, Text, VStack, Flex, Spinner } from '@chakra-ui/react';
import { debounce } from 'lodash';
import config from '../../../config';

interface Address {
	street: string;
	city: string;
	state: string;
	postalCode: string;
	country: string;
	lat: number;
	long: number;
	formatted: string;
	placeId?: string;
	regionCode?: string;
}

interface GoogleMapsAutocompleteProps {
	value: string;
	onChange: (address: Address, formattedAddress: string) => void;
	onInputChange?: (value: string) => void;
	placeholder?: string;
	error?: string;
	label?: string;
	required?: boolean;
	autoSearch?: boolean; // Auto-trigger search and show suggestions when prefilled
}

interface GoogleMapsSuggestion {
	place_id: string;
	description: string;
	structured_formatting: {
		main_text: string;
		secondary_text: string;
	};
	originalSuggestion: any; // Store the original suggestion object
}

declare global {
	interface Window {
		google: any;
		initGoogleMaps: () => void;
	}
}

const GoogleMapsAutocomplete: React.FC<GoogleMapsAutocompleteProps> = ({
	value,
	onChange,
	onInputChange,
	placeholder = 'Enter address...',
	error,
	label,
	required,
	autoSearch = false,
}) => {
	const [inputValue, setInputValue] = useState(value);
	const [suggestions, setSuggestions] = useState<GoogleMapsSuggestion[]>([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [apiLoaded, setApiLoaded] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const autoSearchTriggeredRef = useRef(false);

	// Handle input change - only update local state, don't call parent
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		setInputValue(newValue);
		setShowSuggestions(true);

		if (newValue.trim()) {
			debouncedFetchSuggestions(newValue);
		} else {
			setSuggestions([]);
		}
	};

	// Only sync with parent value when component mounts or parent value changes (not during typing)
	useEffect(() => {
		if (value !== inputValue) {
			setInputValue(value);
		}
	}, [value]); // Only depend on value, not inputValue to prevent loops

	// Load Google Maps API
	useEffect(() => {
		// Check if API key is configured
		const apiKey = config.google.mapsApiKey;
		if (!apiKey) {
			console.error(
				'Google Maps API key not found. Please set VITE_GOOGLE_MAPS_API_KEY in your environment variables.',
			);
			return;
		}

		// Check if already loaded and has importLibrary
		const checkApiLoaded = () => {
			return !!(
				window.google &&
				window.google.maps &&
				window.google.maps.importLibrary
			);
		};

		if (checkApiLoaded()) {
			setApiLoaded(true);
			return;
		}

		// Check if script is already loading
		const existingScript = document.querySelector(
			'script[src*="maps.googleapis.com"]',
		);
		if (existingScript) {
			// Script is loading, wait for it
			const checkLoaded = () => {
				if (checkApiLoaded()) {
					setApiLoaded(true);
				} else {
					setTimeout(checkLoaded, 100);
				}
			};
			checkLoaded();
			return;
		}

		let timeoutId: NodeJS.Timeout | null = null;
		let checkIntervalId: NodeJS.Timeout | null = null;

		// Set up callback
		window.initGoogleMaps = () => {
			// Wait a bit for importLibrary to be available
			const checkLoaded = () => {
				if (checkApiLoaded()) {
					setApiLoaded(true);
					if (checkIntervalId) {
						clearInterval(checkIntervalId);
					}
				} else {
					setTimeout(checkLoaded, 100);
				}
			};
			checkLoaded();
		};

		// Create and load script with places library
		const script = document.createElement('script');
		script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&callback=initGoogleMaps`;
		script.async = true;
		script.defer = true;
		script.onerror = () => {
			console.error('Failed to load Google Maps API');
			if (timeoutId) clearTimeout(timeoutId);
			if (checkIntervalId) clearInterval(checkIntervalId);
		};

		// Set timeout for loading
		timeoutId = setTimeout(() => {
			if (!checkApiLoaded()) {
				console.error('Google Maps API loading timeout');
			}
		}, 10000); // 10 second timeout

		// Also check periodically in case callback doesn't fire (with async loading)
		checkIntervalId = setInterval(() => {
			if (checkApiLoaded()) {
				setApiLoaded(true);
				clearInterval(checkIntervalId!);
				if (timeoutId) clearTimeout(timeoutId);
			}
		}, 200);

		script.onload = () => {
			if (timeoutId) clearTimeout(timeoutId);
		};

		document.head.appendChild(script);

		// Cleanup function
		return () => {
			if (timeoutId) clearTimeout(timeoutId);
			if (checkIntervalId) clearInterval(checkIntervalId);
			// Only clear callback if it was set by this component
			if ((window as any).initGoogleMaps) {
				(window as any).initGoogleMaps = undefined;
			}
		};
	}, []);

	// Debounced function to fetch suggestions using new Places API
	const debouncedFetchSuggestions = useCallback(
		debounce(async (input: string) => {
			if (!input.trim() || !apiLoaded) {
				setSuggestions([]);
				setIsLoading(false);
				return;
			}

			setIsLoading(true);

			// Use new API with importLibrary
			if (window.google?.maps?.importLibrary) {
				// @ts-ignore
				const { AutocompleteSessionToken, AutocompleteSuggestion } =
					await window.google.maps.importLibrary('places');

				// Create a session token
				const token = new AutocompleteSessionToken();

				// Create request
				const request = {
					input: input,
					includedPrimaryTypes: ['establishment', 'geocode'],
					sessionToken: token,
					language: 'en-US',
				};

				// Fetch autocomplete suggestions using the new API
				const { suggestions } =
					await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

				if (suggestions && suggestions.length > 0) {
					// Convert new API format to our suggestion format
					const formattedSuggestions = suggestions.map((suggestion: any) => ({
						place_id:
							suggestion.placePrediction?.placeId ||
							suggestion.placePrediction?.id,
						description:
							suggestion.placePrediction?.text?.text ||
							suggestion.placePrediction?.text?.toString() ||
							'',
						structured_formatting: {
							main_text:
								suggestion.placePrediction?.text?.text ||
								suggestion.placePrediction?.text?.toString() ||
								'',
							secondary_text:
								suggestion.placePrediction?.text?.text ||
								suggestion.placePrediction?.text?.toString() ||
								'',
						},
						// Store the original suggestion for later use
						originalSuggestion: suggestion,
					}));
					setSuggestions(formattedSuggestions);
				} else {
					setSuggestions([]);
				}
				setIsLoading(false);
			} else {
				console.error('🔍 Google Maps importLibrary not available');
				setSuggestions([]);
				setIsLoading(false);
			}
		}, 300),
		[apiLoaded],
	);

	// Auto-search when prefilled value exists and autoSearch is enabled
	useEffect(() => {
		if (
			autoSearch &&
			apiLoaded &&
			value &&
			value.trim() &&
			!autoSearchTriggeredRef.current
		) {
			autoSearchTriggeredRef.current = true;
			// Trigger the search and show suggestions
			setShowSuggestions(true);
			debouncedFetchSuggestions(value);
			// Focus the input so user sees the dropdown
			setTimeout(() => {
				inputRef.current?.focus();
			}, 100);
		}
	}, [autoSearch, apiLoaded, value, debouncedFetchSuggestions]);

	// Reset auto-search flag when value changes significantly
	useEffect(() => {
		if (!value || !value.trim()) {
			autoSearchTriggeredRef.current = false;
		}
	}, [value]);

	// Parse Google Place to Address format (updated for new API)
	const parseGooglePlaceToAddress = (
		place: any,
		originalSuggestion?: string,
	): Address => {
		const addressComponents = place.addressComponents || [];

		const getComponent = (type: string) => {
			const component = addressComponents.find((comp: any) =>
				comp.types.includes(type),
			);
			return component ? component.longText || component.long_name : '';
		};

		const getComponentShortName = (type: string) => {
			const component = addressComponents.find((comp: any) =>
				comp.types.includes(type),
			);
			return component ? component.shortText || component.short_name : '';
		};

		// Extract coordinates - try multiple possible locations
		let lat = 0;
		let lng = 0;

		if (place.location) {
			// New API format - location might be a LatLng object or have lat/lng properties
			if (typeof place.location.lat === 'function') {
				// It's a LatLng object
				lat = place.location.lat();
				lng = place.location.lng();
			} else {
				// It has lat/lng properties
				lat = place.location.lat || place.location.latitude || 0;
				lng = place.location.lng || place.location.longitude || 0;
			}
		} else if (place.geometry?.location) {
			// Legacy API format
			if (typeof place.geometry.location.lat === 'function') {
				lat = place.geometry.location.lat();
				lng = place.geometry.location.lng();
			} else {
				lat =
					place.geometry.location.lat || place.geometry.location.latitude || 0;
				lng =
					place.geometry.location.lng || place.geometry.location.longitude || 0;
			}
		}

		// Enhanced city extraction logic
		const getCityComponent = () => {
			// First, try to extract city from the original suggestion if available
			if (originalSuggestion) {
				// Pattern: "Street, City, State ZIP, Country" or similar
				const parts = originalSuggestion
					.split(',')
					.map((part: string) => part.trim());
				if (parts.length >= 3) {
					// Second part is usually the city in suggestions
					const potentialCity = parts[1];
					// Make sure it's not a state abbreviation (2 letters) or ZIP code (numbers)
					if (
						potentialCity &&
						potentialCity.length > 2 &&
						!/^\d+$/.test(potentialCity) &&
						!/^[A-Z]{2}$/.test(potentialCity)
					) {
						return potentialCity;
					}
				}
			}

			// Try different city-related component types in order of preference
			const cityTypes = [
				'locality',
				'sublocality',
				'sublocality_level_1',
				'administrative_area_level_2',
				'administrative_area_level_3',
			];

			for (const type of cityTypes) {
				const city = getComponent(type);
				if (city) {
					return city;
				}
			}

			// If no city found through standard types, try to extract from formatted address
			const formatted = place.formattedAddress || place.formatted_address || '';
			if (formatted) {
				// Try to extract city from formatted address pattern
				// Pattern: "Street, City, State ZIP, Country"
				const parts = formatted.split(',').map((part: string) => part.trim());
				if (parts.length >= 3) {
					// Second part is usually the city
					const potentialCity = parts[1];
					if (potentialCity && potentialCity !== parts[2]) {
						// Make sure it's not the state
						return potentialCity;
					}
				}
			}

			return '';
		};

		const streetNumber = getComponent('street_number');
		const route = getComponent('route');
		
		let street = '';
		if (streetNumber && route) {
			street = `${streetNumber} ${route}`;
		} else if (streetNumber || route) {
			street = streetNumber || route;
		}
		
		if (!street.trim()) {
			const alternativeTypes = [
				'premise',
				'subpremise',
				'establishment',
				'point_of_interest',
				'park',
				'neighborhood',
			];
			
			for (const type of alternativeTypes) {
				const component = getComponent(type);
				if (component) {
					street = component;
					break;
				}
			}
		}
		
		if (!street.trim() && (place.formattedAddress || place.formatted_address)) {
			const formatted = place.formattedAddress || place.formatted_address;
			const parts = formatted.split(',').map((part: string) => part.trim());
			if (parts.length > 0) {
				const firstPart = parts[0];
				const city = getCityComponent();
				if (firstPart && firstPart !== city) {
					street = firstPart;
				}
			}
		}

		return {
			street: street.trim(),
			city: getCityComponent(),
			state: getComponent('administrative_area_level_1'),
			postalCode: getComponent('postal_code'),
			country: getComponent('country'),
			lat: lat,
			long: lng,
			formatted: place.formattedAddress || place.formatted_address || '',
			placeId: place.id || place.place_id || undefined,
			regionCode: getComponentShortName('country'),
		};
	};

	// Handle suggestion selection using new Places API
	const handleSuggestionSelect = async (suggestion: any) => {
		try {
			// Set loading state
			setIsLoading(true);
			setShowSuggestions(false);

			// Immediately set the input value to the suggestion description for better UX
			const selectedText = suggestion.description || '';
			setInputValue(selectedText);

			// Try new API first (using importLibrary)
			if (window.google?.maps?.importLibrary && suggestion.originalSuggestion) {
				try {
					// @ts-ignore
					const { Place } = await window.google.maps.importLibrary('places');

					// Use the new API: suggestion.placePrediction.toPlace()
					const place = suggestion.originalSuggestion.placePrediction.toPlace();

					// Fetch place details using the new API
					await place.fetchFields({
						fields: [
							'id',
							'displayName',
							'formattedAddress',
							'addressComponents',
							'location',
						],
					});

					const address = parseGooglePlaceToAddress(place, selectedText);

					// Keep the original suggestion text in the input for better UX
					// The user selected this specific text, so we should preserve it
					const displayText = selectedText || address.formatted || '';

					// Don't change the input value - keep the original suggestion
					// setInputValue(displayText); // Commented out to preserve original suggestion

					// Call the parent's onChange with the parsed address
					// Pass the original suggestion text as the display value
					onChange(address, displayText);

					// Also call parent's onInputChange to update with the original suggestion
					if (onInputChange) {
						onInputChange(displayText);
					}
				} catch (newApiError) {
					console.error(
						'❌ Error fetching place details with new API:',
						newApiError,
					);
					// Keep the selected text as fallback
					setInputValue(selectedText);
				}
			} else {
				// If no originalSuggestion, just use the selected text
				setInputValue(selectedText);
			}

			setIsLoading(false);
		} catch (error) {
			console.error('❌ Error in suggestion selection:', error);
			// Keep the selected text as fallback
			setInputValue(suggestion.description || '');
			setIsLoading(false);
		}
	};

	// Handle click outside to close suggestions
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				inputRef.current &&
				!inputRef.current.contains(event.target as Node)
			) {
				setShowSuggestions(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	return (
		<Box ref={inputRef}>
			{label && (
				<Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
					{label}
					{required && (
						<Text as="span" color="red.500" ml={1}>
							*
						</Text>
					)}
				</Text>
			)}
			<Box position="relative">
				<Input
					value={inputValue}
					onChange={handleInputChange}
					placeholder={apiLoaded ? placeholder : 'Loading Google Maps...'}
					isInvalid={!!error}
					isDisabled={!apiLoaded}
					onFocus={() => setShowSuggestions(true)}
					onBlur={() => {
						// Delay hiding suggestions to allow clicking on them
						setTimeout(() => setShowSuggestions(false), 200);
					}}
				/>
				{!apiLoaded && (
					<Box
						position="absolute"
						right={3}
						top="50%"
						transform="translateY(-50%)"
					>
						<Spinner size="sm" color="blue.500" />
					</Box>
				)}
				{isLoading && apiLoaded && (
					<Box
						position="absolute"
						right={3}
						top="50%"
						transform="translateY(-50%)"
					>
						<Spinner size="sm" color="gray.500" />
					</Box>
				)}

				{showSuggestions && (suggestions.length > 0 || isLoading) && (
					<Box
						position="absolute"
						top="100%"
						left={0}
						right={0}
						bg="white"
						border="1px solid"
						borderColor="gray.200"
						borderRadius="md"
						boxShadow="lg"
						zIndex={1000}
						maxH="200px"
						overflowY="auto"
					>
						{isLoading ? (
							<Text p={3} color="gray.500">
								Loading...
							</Text>
						) : (
							<VStack spacing={0} align="stretch">
								{suggestions.map((suggestion, index) => (
									<Box
										key={suggestion.place_id || index}
										p={3}
										cursor="pointer"
										_hover={{ bg: 'gray.50' }}
										onClick={() => handleSuggestionSelect(suggestion)}
									>
										<Text fontSize="sm" fontWeight="medium">
											{suggestion.structured_formatting?.main_text ||
												suggestion.description}
										</Text>
										{suggestion.structured_formatting?.secondary_text && (
											<Text fontSize="xs" color="gray.500">
												{suggestion.structured_formatting.secondary_text}
											</Text>
										)}
									</Box>
								))}
							</VStack>
						)}
					</Box>
				)}
			</Box>
			{error && (
				<Text color="red.500" fontSize="sm" mt={1}>
					{error}
				</Text>
			)}
		</Box>
	);
};

export default GoogleMapsAutocomplete;
